<div class="container-fluid">
  <h2>{{ 'INDICATOR.RISK_MANAGEMENT' | translate }}</h2>

  <nav ngbNav #nav="ngbNav" class="nav-tabs" [destroyOnHide]="false" [(activeId)]="activeTabId">
    @for (year of years; track year; let i = $index) {
      <ng-container [ngbNavItem]="i">
        <a ngbNavLink>{{ year }}</a>
        <ng-template ngbNavContent>
          <!-- Mobile toolbar: filter toggle + XLS side by side (mobile only) -->
          @if (isMobile$ | async) {
            <div class="rdf-toolbar-row mt-3">
              <div class="rdf-mobile-filter-toggle"
                (click)="mobileFiltersOpen = !mobileFiltersOpen">
                <div style="display:flex; align-items:center; gap:7px; font-size:13px; font-weight:500; color:var(--navy-600);">
                  <lucide-icon name="funnel" size="14"></lucide-icon>
                  <span>{{ 'DATATABLE.FILTERS' | translate }}</span>
                  @if (riskActiveFilterCount > 0) {
                    <span class="rdf-mobile-filter-badge">{{ riskActiveFilterCount }}</span>
                  }
                </div>
                <lucide-icon [name]="mobileFiltersOpen ? 'chevron-up' : 'chevron-down'" style="color:var(--fg-4);" size="14"></lucide-icon>
              </div>
              @if (records[year]?.length) {
                <button class="btn btn-sm btn-outline-success"
                  [disabled]="exporting[year]"
                  (click)="exportCompanyScores(year)">
                  @if (exporting[year]) {
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  }
                  @if (!exporting[year]) {
                    <lucide-icon name="file-spreadsheet" size="16"></lucide-icon>
                    }&nbsp;XLS
                  </button>
                }
              </div>
            }
            <!-- Filter row — always on desktop, collapsible on mobile -->
            <div class="rdf-filter-row mt-3 mb-2"
              [class.d-none]="(isMobile$ | async) && !mobileFiltersOpen">
              <div class="rdf-filter-left">
                <label class="mb-0 fw-semibold me-1">{{ 'RISK-RESULT.SECTOR' | translate }}:</label>
                <select class="form-select form-select-sm" style="width: auto;"
                  [(ngModel)]="selectedSector[year]" (change)="loadCompanies(year)">
                  @for (s of sectors; track s) {
                    <option [value]="s.name">{{ s.label }}</option>
                  }
                </select>
                <input class="form-control form-control-sm rdf-search-input"
                  type="text"
                  placeholder="{{ 'RISK-RESULT.SEARCH_PLACEHOLDER' | translate }}"
                  [(ngModel)]="search[year]"
                  (ngModelChange)="onSearchChange(year)">
                <div class="btn-group btn-group-sm" role="group">
                  <button type="button" class="btn"
                    [class.btn-secondary]="!colorFilter[year]"
                    [class.btn-outline-secondary]="colorFilter[year]"
                  (click)="setColorFilter(year, '')">{{ 'RISK-RESULT.ALL' | translate }}</button>
                  <button type="button" class="btn"
                    [class.btn-danger]="colorFilter[year] === 'red'"
                    [class.btn-outline-danger]="colorFilter[year] !== 'red'"
                  (click)="setColorFilter(year, 'red')">&#9679;</button>
                  <button type="button" class="btn"
                    [class.btn-warning]="colorFilter[year] === 'orange'"
                    [class.btn-outline-warning]="colorFilter[year] !== 'orange'"
                  (click)="setColorFilter(year, 'orange')">&#9679;</button>
                  <button type="button" class="btn"
                    [class.btn-success]="colorFilter[year] === 'green'"
                    [class.btn-outline-success]="colorFilter[year] !== 'green'"
                  (click)="setColorFilter(year, 'green')">&#9679;</button>
                </div>
              </div>
              <!-- XLS: desktop only — mobile gets it in the toolbar above -->
              @if (records[year]?.length) {
                <button class="btn btn-sm btn-outline-success"
                  [disabled]="exporting[year]"
                  (click)="exportCompanyScores(year)">
                  @if (exporting[year]) {
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  }
                  @if (!exporting[year]) {
                    <lucide-icon name="file-spreadsheet" size="16"></lucide-icon>
                    }&nbsp;{{ 'CSV.EXCEL_DOWNLOAD' | translate }}
                  </button>
                }
              </div>
              <!-- Loading overlay -->
              <div style="position: relative; width: 100%;">
                @if (loading[year]) {
                  <div
                    style="position: absolute; inset: 0; background: rgba(255,255,255,0.65); z-index: 10; display: flex; align-items: center; justify-content: center; border-radius: 4px; min-height: 80px;">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">{{ 'UTIL.LOADING' | translate }}</span>
                    </div>
                  </div>
                }
                <!-- ── Desktop table view ── -->
                @if (!(isMobile$ | async)) {
                  <div class="rdf-table-responsive">
                    <table class="table table-sm table-hover align-middle mb-0">
                      <thead class="table-dark">
                        <tr>
                          <th style="width: 120px;"></th>
                          <th>{{ 'RISK-RESULT.TAX_ID' | translate }}</th>
                          <th>{{ 'RISK-RESULT.COMPANY_NAME' | translate }}</th>
                          <th class="text-end">{{ 'RISK-RESULT.SCORE' | translate }}</th>
                          @for (ind of indicators[year]; track ind) {
                            <th class="text-end">
                              {{ ind.name }}
                            </th>
                          }
                        </tr>
                      </thead>
                      <tbody>
                        @for (row of records[year]; track row) {
                          <tr>
                            <td>
                              <div class="d-flex gap-1">
                                <button class="btn btn-sm btn-outline-primary text-nowrap"
                                  (click)="goToBreakdown(row, year)">
                                  <lucide-icon name="chart-line" class="me-1" size="16"></lucide-icon>{{ 'RISK-RESULT.VIEW' | translate }}
                                </button>
                                @if (row.payeeId) {
                                  <button class="btn btn-sm btn-outline-secondary text-nowrap"
                                    (click)="goToProfile(row)">
                                    <lucide-icon name="building-2" class="me-1" size="16"></lucide-icon>{{ 'RISK-RESULT.PROFILE' | translate }}
                                  </button>
                                }
                              </div>
                            </td>
                            <td class="font-monospace small border"
                      [ngClass]="{
                        'table-danger':  row.color === 'red',
                        'table-warning': row.color === 'orange',
                        'table-success': row.color === 'green'
                      }">{{ row.match }}</td>
                            <td>{{ row.companyName }}</td>
                            <td class="text-end fw-bold">{{ row.totalScore | number:'1.2-2' }}</td>
                            @for (ind of indicators[year]; track ind) {
                              <td class="text-end">
                                {{ getIndicatorScore(row, ind.id) | number:'1.2-2' }}
                              </td>
                            }
                          </tr>
                        }
                        @if (!records[year]?.length) {
                          <tr>
                            <td [attr.colspan]="4 + (indicators[year]?.length || 0)"
                              class="text-center text-muted py-3">
                              {{ 'RISK-RESULT.NO_DATA' | translate }}
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
                <!-- ── Mobile card view ── -->
                @if (isMobile$ | async) {
                  <div class="rdf-card-grid">
                    @if (!records[year]?.length) {
                      <div class="text-center text-muted py-4">
                        {{ 'RISK-RESULT.NO_DATA' | translate }}
                      </div>
                    }
                    @for (row of records[year]; track row) {
                      <div
                        class="rdf-risk-card"
                        [class.rdf-card-red]="row.color === 'red'"
                        [class.rdf-card-orange]="row.color === 'orange'"
                        [class.rdf-card-green]="row.color === 'green'">
                        <!-- Card header: company + score -->
                        <div class="rdf-card-header">
                          <div class="rdf-card-company">{{ row.companyName }}</div>
                          <div class="rdf-card-score"
                            [class.text-danger]="row.color === 'red'"
                            [class.text-warning]="row.color === 'orange'"
                            [class.text-success]="row.color === 'green'">
                            {{ row.totalScore | number:'1.2-2' }}
                          </div>
                        </div>
                        <!-- Tax ID -->
                        <div class="rdf-card-taxid">{{ row.match }}</div>
                        <!-- Indicator scores -->
                        @if (indicators[year]?.length) {
                          <div class="rdf-card-indicators">
                            @for (ind of indicators[year]; track ind) {
                              <div class="rdf-card-ind-row">
                                <span class="rdf-card-ind-name">{{ ind.name }}</span>
                                <span class="rdf-card-ind-score">{{ getIndicatorScore(row, ind.id) | number:'1.2-2' }}</span>
                              </div>
                            }
                          </div>
                        }
                        <!-- Action buttons -->
                        <div class="rdf-card-actions">
                          <button class="btn btn-sm btn-outline-primary"
                            (click)="goToBreakdown(row, year)">
                            <lucide-icon name="chart-line" class="me-1" size="16"></lucide-icon>{{ 'RISK-RESULT.VIEW' | translate }}
                          </button>
                          @if (row.payeeId) {
                            <button class="btn btn-sm btn-outline-secondary"
                              (click)="goToProfile(row)">
                              <lucide-icon name="building-2" class="me-1" size="16"></lucide-icon>{{ 'RISK-RESULT.PROFILE' | translate }}
                            </button>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div><!-- /relative wrapper -->
              <!-- Pagination -->
              @if (total[year] > pageSize) {
                <div class="rdf-pagination-row mt-2">
                  <span class="rdf-page-info">{{ total[year] }} {{ 'RISK-RESULT.COMPANIES' | translate }}</span>
                  <ngb-pagination
                    [page]="page[year]"
                    [pageSize]="pageSize"
                    [collectionSize]="total[year]"
                    [maxSize]="(isMobile$ | async) ? 3 : 5"
                    [rotate]="true"
                    [ellipses]="true"
                    (pageChange)="onPageChange(year, $event)">
                  </ngb-pagination>
                </div>
              }
            </ng-template>
          </ng-container>
        }
      </nav>
      <div [ngbNavOutlet]="nav" class="mt-0"></div>
    </div>
